import apiClient from '../config/api';
import { getCurrentProfile, subscribeCurrentProfile } from './currentProfile';
import {
  getCounties,
  getTicketStatuses,
  getTicketPriorities,
  getTicketPrograms,
  getTicketIssueTypes,
  getTicketAssignees,
  getTicketFilterAssignees,
} from './lookups';

const optionsCache = new Map();
const listRequests = new Map();
const contextCache = new Map();
const optionsTTL = 2 * 60 * 1000;
const profileKey = () => {
  const profile = getCurrentProfile();
  return profile ? JSON.stringify([profile.userID, profile.username, profile.isAdmin, profile.isit, profile.countyID]) : null;
};
let accountKey = profileKey();
subscribeCurrentProfile(() => {
  const nextKey = profileKey();
  if (nextKey === null || nextKey !== accountKey) {
    optionsCache.clear(); contextCache.clear(); listRequests.clear();
  }
  accountKey = nextKey;
});
const cachedOptions = (cache, key, fetcher) => {
  const existing = cache.get(key);
  if (existing && existing.expires > Date.now()) return existing.request;
  const entry = { expires: Infinity };
  entry.request = Promise.resolve().then(fetcher).then((result) => {
    entry.expires = Date.now() + optionsTTL;
    return result;
  }).catch((error) => {
    if (cache.get(key) === entry) cache.delete(key);
    throw error;
  });
  cache.set(key, entry);
  return entry.request;
};
export const getTicketContext = () => cachedOptions(contextCache, 'context', () => apiClient.get('/tickets/context'));

// Assemble the existing view model from dedicated lookup APIs and user context.
const fetchTicketOptions = async (assignees) => {
  const contextResponse = await getTicketContext();
  const context = contextResponse.data;
  const empty = Promise.resolve({ data: [] });
  const [statuses, priorities, programs, counties, users, issueTypes] = await Promise.all([
    getTicketStatuses(),
    getTicketPriorities(),
    context.canUseITControls ? getTicketPrograms() : empty,
    context.canManage ? getCounties() : empty,
    context.canManage && assignees !== 'none'
      ? (assignees === 'assignable' ? getTicketAssignees() : getTicketFilterAssignees())
      : empty,
    context.canUseITControls ? getTicketIssueTypes() : empty,
  ]);

  return {
    data: {
      ...context,
      statuses: statuses.data,
      issueTypes: issueTypes.data,
      priorities: priorities.data,
      programs: programs.data,
      counties: counties.data.map((county) => ({ ...county, DisplayName: county.Name })),
      assignees: users.data,
    },
  };
};
export const getTicketOptions = ({ assignees = 'filter' } = {}) => (
  cachedOptions(optionsCache, assignees, () => fetchTicketOptions(assignees))
);
export const getTickets = (params = {}) => {
  const key = JSON.stringify(Object.entries(params).sort(([a], [b]) => a.localeCompare(b)));
  if (listRequests.has(key)) return listRequests.get(key);
  const request = apiClient.get('/tickets', { params }).finally(() => {
    if (listRequests.get(key) === request) listRequests.delete(key);
  });
  listRequests.set(key, request);
  return request;
};
export const getTicket = (ticketId) => apiClient.get(`/tickets/${ticketId}`);
const afterTicketMutation = (request) => request.then((result) => { listRequests.clear(); return result; });
export const createTicket = (payload) => afterTicketMutation(apiClient.post('/tickets', payload));
export const updateTicket = (ticketId, payload) => afterTicketMutation(apiClient.patch(`/tickets/${ticketId}`, payload));
export const setTicketAssignees = (ticketId, userIds) => (
  afterTicketMutation(apiClient.put(`/tickets/${ticketId}/assignees`, { userIds }))
);
export const addTicketComment = (ticketId, threadId, body) => (
  apiClient.post(`/tickets/${ticketId}/threads/${threadId}/comments`, { body })
);
export const uploadTicketImages = (ticketId, files) => {
  const formData = new FormData();
  Array.from(files || []).forEach((file) => formData.append('images', file));
  return apiClient.post(`/tickets/${ticketId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getTicketImage = (ticketId, attachmentId) => (
  apiClient.get(`/tickets/${ticketId}/attachments/${attachmentId}`, { responseType: 'blob' })
);

export const addTicketNote = (ticketId, visibility, body) => (
  apiClient.post(`/tickets/${ticketId}/notes`, { visibility, body })
);
